import { Component, computed, effect, input, model, output } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { SectionHeaderComponent } from "../../../../shared/components/section-header/section-header.component";
import { FormFieldComponent } from "../../../../shared/components/form-field/form-field.component";
import { HorizontalButton, HorizontalButtonGroupComponent } from "../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { IssueCardComponent } from "../issue-card/issue-card.component";
import { SeverityInfoComponent } from "../../../../shared/components/severity-info/severity-info.component";
import { AssertionIssue, Media } from "../../../../core/database/db";

interface SeverityLevel {
  label: string;
  value: string;
}

const SEVERITY_LEVELS: SeverityLevel[] = [
  { label: "Minor", value: "minor" },
  { label: "Major", value: "major" },
  { label: "Missing", value: "missing" }
];

/**
 * Confirmation step for a single carried-over issue. Shows the read-only issue card (no checkbox), a
 * "Issue X of Y" header with a back affordance, and a required severity selector (Minor / Major / Missing).
 * The host owns the stepper navigation buttons; this component only captures the severity choice.
 */
@Component({
  selector: "app-issue-confirmation",
  standalone: true,
  imports: [
    SectionHeaderComponent,
    FormFieldComponent,
    ReactiveFormsModule,
    HorizontalButtonGroupComponent,
    IssueCardComponent,
    SeverityInfoComponent
  ],
  templateUrl: "./issue-confirmation.component.html",
  styleUrl: "./issue-confirmation.component.scss"
})
export class IssueConfirmationComponent {
  issue = input.required<AssertionIssue>();
  media = input<Media[]>([]);
  /** Zero-based index of the issue currently being confirmed. */
  currentIndex = input<number>(0);
  /** Total number of issues being confirmed in this pass. */
  total = input<number>(0);
  /** Two-way selected severity (minor | major | missing | ""). */
  severity = model<string>("");
  /** Set by the host when the user tries to advance; only then is the required-severity error shown. */
  showErrors = input<boolean>(false);
  testId = input<string>("app-issue-confirmation");

  readonly back = output<void>();

  /** Reactive form backing the severity field so app-form-field derives its error like bq9-not-complete. */
  protected readonly form = new FormGroup({
    severity: new FormControl<string>("", { nonNullable: true, validators: Validators.required })
  });

  constructor() {
    // Mirror the two-way severity model into the reactive control so app-form-field can surface the
    // required-error message while the host keeps binding severity directly.
    effect(() => {
      const severity = this.severity();
      if (this.form.controls.severity.value !== severity) {
        this.form.controls.severity.setValue(severity, { emitEvent: false });
      }
    });
  }

  protected headerTitle = computed(() => `Issue ${this.currentIndex() + 1} of ${this.total()}`);

  /** The severity field only shows as invalid after a submit attempt with no choice (not on entry). */
  protected readonly severityInvalid = computed(() => this.showErrors() && !this.severity());

  /** Minor / Major / Missing rendered as a single-select horizontal button group. */
  protected readonly severityButtons = computed<HorizontalButton[]>(() =>
    SEVERITY_LEVELS.map(level => ({
      label: level.label,
      value: level.value,
      styleClass: this.severity() === level.value
        ? "bqa-severity-btn bqa-severity-btn-selected"
        : "bqa-severity-btn",
      action: () => this.selectSeverity(level.value),
      testId: `${this.testId()}-severity-${level.value}`
    }))
  );

  protected selectSeverity(value: string): void {
    this.severity.set(value);
  }
}
